// frontend/src/pages/admin/AdminZones.js
import React, { useState, useEffect, useMemo } from 'react';
import { zonesApi } from '../../api/zones';
import { FaPlus, FaEdit, FaTrash, FaTimes, FaSpinner } from 'react-icons/fa';

const initialFormState = {
    zone_name: '',
    lga_id: '',
    description: '',
    is_active: true,
};

// --- PREDEFINED ZONE DATA ---
const ZONE_SUGGESTIONS = {
    // --- LAGOS ---
    "Ikeja": ["Allen Avenue", "Opebi", "Alausa", "Ikeja GRA", "Toyin Street", "Adeniyi Jones", "Computer Village", "Agidingbi", "Maryland"],
    "Eti-Osa": ["Lekki Phase 1", "Victoria Island", "Ikoyi", "Ajah", "Sangotedo", "Chevron Drive", "VGC", "Jakande", "Osapa London"],
    "Lagos Mainland": ["Yaba", "Ebute Metta", "Surulere (Mainland)", "Akoka", "Sabo", "Alagomeji"],
    "Surulere": ["Adeniran Ogunsanya", "Bode Thomas", "Ojuelegba", "Stadium", "Iponri", "Aguda"],
    "Kosofe": ["Magodo Phase 1", "Magodo Phase 2", "Ogudu", "Ketu", "Mile 12", "Ojota"],
    "Ikorodu": ["Ikorodu Garage", "Agric", "Ogolonto", "Igbogbo", "Imota"],
    "Alimosho": ["Egbeda", "Ikotun", "Igando", "Dopemu", "Akowonjo", "Ipaja"],

    // --- FCT (ABUJA) ---
    "Abuja Municipal (AMAC)": ["Central Business District", "Garki 1", "Garki 2", "Wuse 1", "Wuse 2", "Maitama", "Asokoro", "Gwarinpa", "Utako", "Jabi", "Lokogoma", "Lugbe"],
    "Bwari": ["Bwari Central", "Dutse", "Kubwa", "Ushafa"],
    "Gwagwalada": ["Gwagwalada Central", "University of Abuja Area"],

    // --- EDO ---
    "Oredo": ["Ring Road", "GRA", "Airport Road", "Sapele Road", "Mission Road", "New Benin", "Adesuwa"],
    "Ikpoba Okha": ["Upper Sakponba", "Aduwawa", "Ikpoba Hill", "Ramat Park"],
    "Egor": ["Ugbowo (UNIBEN)", "Use", "Ogida", "Siluko Road"],

    // --- RIVERS (PORT HARCOURT) ---
    "Port Harcourt": ["Old GRA", "New GRA", "Trans Amadi", "Diobu", "D-Line", "Borikiri", "Aba Road"],
    "Obio/Akpor": ["Rumuokoro", "Rumuola", "GRA Phase 2", "Woji", "Elelenwo", "Choba (UNIPORT)"],

    // --- KANO ---
    "Kano Municipal": ["Kofar Mata", "Gandun Albasa", "Shahuci", "Mandawari"],
    "Fagge": ["Sabon Gari", "Kwari Market", "France Road"],
    "Nasarawa": ["Bompai", "Giginyu", "Hotoro", "Kawo"],

    // --- OYO (IBADAN) ---
    "Ibadan North": ["Bodija", "Agodi GRA", "Mokola", "University of Ibadan"],
    "Ibadan South-West": ["Ring Road", "Dugbe", "Iyaganku", "Apata"],
    "Ibadan North-East": ["Iwo Road", "Agodi Gate"],

    // --- ENUGU ---
    "Enugu North": ["Independence Layout", "GRA", "New Haven", "Ogui Road"],
    "Enugu South": ["Achara Layout", "Uwani", "Amechi"],

    // --- KADUNA ---
    "Kaduna North": ["Ungwan Rimi", "Kabala Costain", "Kawo"],
    "Kaduna South": ["Barnawa", "Kakuri", "Makera"],
    "Chikun": ["Ungwan Romi", "Sabon Tasha", "Narayi"]
};

const GENERIC_ZONES = [
    "Zone A", "Zone B", "Zone C", "Central Hub", "North Wing", "South Wing", "East Wing", "West Wing", "Industrial Layout", "Market Area", "Residential Area"
];

function AdminZones() {
    const [zones, setZones] = useState([]);
    const [lgas, setLgas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal & Form State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingZone, setEditingZone] = useState(null);
    const [formData, setFormData] = useState(initialFormState);
    const [saving, setSaving] = useState(false);
    
    // Logic for Custom Zones
    const [availableZoneOptions, setAvailableZoneOptions] = useState([]);
    const [isCustomZone, setIsCustomZone] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [zonesData, lgasData] = await Promise.all([
                    zonesApi.getAllZones(),
                    zonesApi.getAllLgas(),
                ]);
                setZones(zonesData);
                setLgas(lgasData);
            } catch (err) {
                setError("Failed to load zone management data. Please try again.");
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Helper to get options for a specific LGA ID
    const getOptionsForLGA = (lgaId) => {
        if (!lgaId) return [];
        const selectedLgaObj = lgas.find(l => l.id === lgaId);
        if (!selectedLgaObj) return [];

        const specificOptions = ZONE_SUGGESTIONS[selectedLgaObj.lga];
        let options = specificOptions ? [...specificOptions, ...GENERIC_ZONES] : [...GENERIC_ZONES];
        return options;
    };

    // Update available zone options whenever the selected LGA changes
    useEffect(() => {
        if (formData.lga_id) {
            const options = getOptionsForLGA(formData.lga_id);
            setAvailableZoneOptions(options);
        } else {
            setAvailableZoneOptions([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.lga_id, lgas]);

    const groupedLgas = useMemo(() => {
        return lgas.reduce((acc, lga) => {
            if (!lga.state || lga.state.trim() === '' || !lga.lga) {
                return acc;
            }
            const stateKey = lga.state.trim();
            if (!acc[stateKey]) {
                acc[stateKey] = [];
            }
            acc[stateKey].push(lga);
            return acc;
        }, {});
    }, [lgas]);

    const handleOpenModal = (zone = null) => {
        if (zone) {
            setEditingZone(zone);
            
            const options = getOptionsForLGA(zone.lga_id);
            const isPredefined = options.includes(zone.zone_name);

            setFormData({
                zone_name: zone.zone_name,
                lga_id: zone.lga_id,
                description: zone.description || '',
                is_active: zone.is_active,
            });
            
            setIsCustomZone(!isPredefined);
        } else {
            setEditingZone(null);
            setFormData(initialFormState);
            setIsCustomZone(false);
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingZone(null);
        setFormData(initialFormState);
        setIsCustomZone(false);
        setError(null);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (name === 'lga_id') {
            setFormData(prev => ({ ...prev, lga_id: value, zone_name: '' }));
            setIsCustomZone(false);
        } else {
            setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        }
    };

    const handleZoneDropdownChange = (e) => {
        const value = e.target.value;
        if (value === 'Others') {
            setIsCustomZone(true);
            setFormData(prev => ({ ...prev, zone_name: '' }));
        } else {
            setIsCustomZone(false);
            setFormData(prev => ({ ...prev, zone_name: value }));
        }
    };

    const handleCustomZoneNameChange = (e) => {
        setFormData(prev => ({ ...prev, zone_name: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.zone_name.trim()) {
            alert("Please enter a Zone Name.");
            return;
        }

        setSaving(true);
        setError(null);
        try {
            if (editingZone) {
                const payload = {
                    zone_name: formData.zone_name,
                    description: formData.description,
                    is_active: formData.is_active,
                };
                await zonesApi.updateZone(editingZone.id, payload);
            } else {
                await zonesApi.createZone(formData);
            }
            const updatedZones = await zonesApi.getAllZones();
            setZones(updatedZones);
            handleCloseModal();
        } catch (err) {
            setError(err.data?.message || `Failed to save zone.`);
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (zoneId) => {
        if (window.confirm("Are you sure you want to delete this zone? This action cannot be undone.")) {
            try {
                await zonesApi.deleteZone(zoneId);
                setZones(prev => prev.filter(z => z.id !== zoneId));
            } catch (err) {
                alert(err.data?.message || "Failed to delete zone.");
            }
        }
    };

    if (loading) return <div className="p-8 text-center"><FaSpinner className="animate-spin text-2xl text-primary" /> Loading Zone Data...</div>;

    return (
        <div>
            {/* UPDATED HEADER: Stack on mobile, Row on desktop */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <h1 className="text-2xl font-bold text-gray-800">Zone Management</h1>
                <button 
                    onClick={() => handleOpenModal()} 
                    className="bg-primary hover:bg-primary-dark text-white font-bold py-2 px-4 rounded flex items-center justify-center gap-2 w-full sm:w-auto"
                >
                    <FaPlus /> Create Zone
                </button>
            </div>

            {/* TABLE - Added Horizontal Scroll */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[800px]">
                    <thead>
                        <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider border-b border-gray-200">
                            <th className="p-4">Zone Name</th>
                            <th className="p-4">LGA</th>
                            <th className="p-4">State</th>
                            <th className="p-4">Status</th>
                            <th className="p-4 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {zones.map(zone => (
                            <tr key={zone.id} className="hover:bg-gray-50">
                                <td className="p-4 font-bold text-gray-800">{zone.zone_name}</td>
                                <td className="p-4 text-sm text-gray-600">{zone.lga_name}</td>
                                <td className="p-4 text-sm text-gray-600">{zone.state_name}</td>
                                <td className="p-4">
                                    {zone.is_active ? (
                                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">Active</span>
                                    ) : (
                                        <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full font-medium">Inactive</span>
                                    )}
                                </td>
                                <td className="p-4 text-right">
                                    <div className="flex justify-end gap-2">
                                        <button onClick={() => handleOpenModal(zone)} className="p-2 text-blue-600 hover:bg-blue-100 rounded"><FaEdit /></button>
                                        <button onClick={() => handleDelete(zone.id)} className="p-2 text-red-600 hover:bg-red-100 rounded"><FaTrash /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* --- ADD/EDIT MODAL --- */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center p-6 border-b">
                            <h3 className="text-xl font-bold">{editingZone ? 'Edit Zone' : 'Create New Zone'}</h3>
                            <button onClick={handleCloseModal}><FaTimes /></button>
                        </div>
                        
                        <form onSubmit={handleSubmit} className="overflow-y-auto p-6 space-y-4">
                            {error && <div className="bg-red-100 text-red-700 p-3 rounded text-sm">{error}</div>}
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">LGA *</label>
                                    <select 
                                        name="lga_id" 
                                        value={formData.lga_id} 
                                        onChange={handleInputChange} 
                                        required 
                                        disabled={!!editingZone} 
                                        className="p-2 border rounded w-full bg-white disabled:bg-gray-100"
                                    >
                                        <option value="">Select an LGA</option>
                                        {Object.keys(groupedLgas).sort().map(state => (
                                            <optgroup label={state} key={state}>
                                                {groupedLgas[state].map(lga => (
                                                    <option key={lga.id} value={lga.id}>{lga.lga}</option>
                                                ))}
                                            </optgroup>
                                        ))}
                                    </select>
                                    {editingZone && <p className="text-xs text-gray-500 mt-1">LGA cannot be changed after creation.</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Zone Name *</label>
                                    
                                    {/* 1. Dropdown Selection */}
                                    {formData.lga_id ? (
                                        <select 
                                            value={isCustomZone ? 'Others' : formData.zone_name} 
                                            onChange={handleZoneDropdownChange} 
                                            required 
                                            className="p-2 border rounded w-full bg-white mb-2"
                                        >
                                            <option value="">Select a Zone</option>
                                            {availableZoneOptions.map((option, index) => (
                                                <option key={index} value={option}>{option}</option>
                                            ))}
                                            <option value="Others" className="font-bold text-blue-600">Others (Specify)</option>
                                        </select>
                                    ) : (
                                        <input 
                                            type="text" 
                                            placeholder="Select an LGA first" 
                                            disabled 
                                            className="p-2 border rounded w-full bg-gray-100 cursor-not-allowed" 
                                        />
                                    )}

                                    {/* 2. Custom Input (Only if 'Others' is selected) */}
                                    {isCustomZone && (
                                        <div className="animate-fadeInUp">
                                            <input 
                                                type="text" 
                                                value={formData.zone_name} 
                                                onChange={handleCustomZoneNameChange}
                                                placeholder="Enter custom zone name..." 
                                                required
                                                className="p-2 border border-blue-300 rounded w-full focus:ring-2 focus:ring-blue-500 focus:outline-none" 
                                                autoFocus
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                                <textarea name="description" value={formData.description} onChange={handleInputChange} className="p-2 border rounded w-full h-20"></textarea>
                            </div>

                            <div>
                                <label className="flex items-center gap-2">
                                    <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleInputChange} className="h-4 w-4 text-primary rounded" />
                                    <span className="text-sm font-medium text-gray-700">Zone is Active</span>
                                </label>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <button type="button" onClick={handleCloseModal} className="px-4 py-2 text-gray-600">Cancel</button>
                                <button type="submit" disabled={saving} className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark">
                                    {saving ? 'Saving...' : 'Save Zone'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminZones;